import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './company/Header';
import { motion } from 'framer-motion';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/projects/`;

const AddProject = () => {
  const [formData, setFormData] = useState({
    project_manager: '', country: '', start_date: '', end_date: '',
    project_number: '', type_of_turbine: '', number_of_turbines: '',
    customer: '', max_days_per_employers: '', name: '', color: '#000000',
    comment: ''
  });
  const [projects, setProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [turbineCoordinates, setTurbineCoordinates] = useState([]);

  useEffect(() => {
    fetchProjects();
  }, []);

  // Check if current formData has number_of_turbines and create coordinate fields
  useEffect(() => {
    if (formData.number_of_turbines && formData.number_of_turbines.length > 0 && turbineCoordinates.length === 0) {
      const numTurbines = parseInt(formData.number_of_turbines) || 0;
      if (numTurbines > 0) {
        const projectName = formData.name || 'Project';
        const newCoordinates = Array.from({ length: numTurbines }, (_, index) => ({
          turbine_number: `${projectName}-T${index + 1}`,
          latitude: '',
          longitude: ''
        }));
        setTurbineCoordinates(newCoordinates);
      }
    }
  }, [formData.number_of_turbines, formData.name]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(API_BASE_URL);
      if (Array.isArray(response.data)) {
        setProjects(response.data);
      } else {
        setError('Error fetching projects. Unexpected data format.');
      }
    } catch (error) {
      setError(`Error fetching projects: ${error.message}`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Handle turbine number change
    if (name === 'number_of_turbines') {
      const numTurbines = parseInt(value) || 0;
      if (numTurbines > 0) {
        // Create array for turbine coordinates
        const projectName = formData.name || 'Project';
        const newCoordinates = Array.from({ length: numTurbines }, (_, index) => ({
          turbine_number: `${projectName}-T${index + 1}`,
          latitude: '',
          longitude: ''
        }));
        setTurbineCoordinates(newCoordinates);
      } else {
        setTurbineCoordinates([]);
      }
    }

    // Handle project name change - update turbine numbers if coordinates exist
    if (name === 'name' && turbineCoordinates.length > 0) {
      const projectName = value || 'Project';
      const updatedCoordinates = turbineCoordinates.map((coord, index) => ({
        ...coord,
        turbine_number: `${projectName}-T${index + 1}`
      }));
      setTurbineCoordinates(updatedCoordinates);
    }
  };

  const handleTurbineCoordinateChange = (index, field, value) => {
    const newCoordinates = [...turbineCoordinates];
    newCoordinates[index] = { ...newCoordinates[index], [field]: value };
    setTurbineCoordinates(newCoordinates);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    try {
      // First create/update the project
      let response;
      if (editingProject) {
        response = await axios.put(`${API_BASE_URL}${editingProject.id}/`, formData, {
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        response = await axios.post(API_BASE_URL, formData, {
          headers: { 'Content-Type': 'application/json' },
        });
      }

      if (response.status === 201 || response.status === 200) {
        const projectId = response.data.id;

        // Now create/update turbines if provided
        if (turbineCoordinates.length > 0) {
          const turbineApiUrl = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/turbines/`;
          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          };

          for (const turbine of turbineCoordinates) {
            const turbineData = {
              turbine_number: turbine.turbine_number,
              name: `${formData.name} - ${turbine.turbine_number}`,
              location: `${formData.name} - ${turbine.turbine_number}`,
              // Required minimum data for API
              model: 1, // Default model ID
              type: 1, // Default type ID
              wind_farm: 1, // Default wind farm ID
              height: 100, // Default height
              power: 2000 // Default power
            };

            // Add coordinates if provided
            if (turbine.latitude && turbine.longitude) {
              turbineData.latitude = parseFloat(turbine.latitude);
              turbineData.longitude = parseFloat(turbine.longitude);
            }

            try {
              await axios.post(turbineApiUrl, turbineData, { headers });
              console.log(`Created turbine: ${turbine.turbine_number}`);
            } catch (turbineError) {
              console.error('Error creating turbine:', turbineError);
              // Continue with other turbines even if one fails
            }
          }
        }

        setSuccess(true);
        setFormData({
          project_manager: '', country: '', start_date: '', end_date: '',
          project_number: '', type_of_turbine: '', number_of_turbines: '',
          customer: '', max_days_per_employers: '', name: '', color: '#000000',
          comment: ''
        });
        setTurbineCoordinates([]);
        setEditingProject(null);
        fetchProjects(); // Refresh the projects list
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('There was an error submitting the form. Please try again.');
    }
  };

  const handleEdit = async (project) => {
    setFormData(project);
    setEditingProject(project);

    // Load existing turbines for this project if any
    const numTurbines = parseInt(project.number_of_turbines) || 0;
    if (numTurbines > 0) {
      try {
        const turbineApiUrl = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/turbines/`;
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        };
        const turbinesResponse = await axios.get(turbineApiUrl, { headers });

        // Filter turbines that might belong to this project (by turbine_number pattern)
        const projectTurbines = turbinesResponse.data.filter(turbine =>
          turbine.turbine_number && turbine.turbine_number.includes(project.name)
        );

        if (projectTurbines.length > 0) {
          const coordinates = projectTurbines.map(turbine => ({
            turbine_number: turbine.turbine_number,
            latitude: turbine.latitude || '',
            longitude: turbine.longitude || ''
          }));
          setTurbineCoordinates(coordinates);
        } else {
          // No existing turbines found, create empty ones based on number_of_turbines
          const newCoordinates = Array.from({ length: numTurbines }, (_, index) => ({
            turbine_number: `${project.name}-T${index + 1}`,
            latitude: '',
            longitude: ''
          }));
          setTurbineCoordinates(newCoordinates);
        }
      } catch (error) {
        console.error('Error loading turbines for project:', error);
        // Even if API fails, create empty turbine fields
        const newCoordinates = Array.from({ length: numTurbines }, (_, index) => ({
          turbine_number: `${project.name}-T${index + 1}`,
          latitude: '',
          longitude: ''
        }));
        setTurbineCoordinates(newCoordinates);
      }
    } else {
      // No turbines for this project, clear the fields
      setTurbineCoordinates([]);
    }
  };

  const handleDelete = async (projectId) => {
    try {
      await axios.delete(`${API_BASE_URL}${projectId}/`);
      fetchProjects(); // Refresh the projects list after deletion
    } catch (error) {
      setError('Error deleting project. Please try again.');
    }
  };

  const inputFields = [
    { name: 'name', label: 'Project Name', type: 'text' },
    { name: 'project_manager', label: 'Project Manager', type: 'text' },
    { name: 'country', label: 'Country', type: 'text' },
    { name: 'start_date', label: 'Start Date', type: 'date' },
    { name: 'end_date', label: 'End Date', type: 'date' },
    { name: 'project_number', label: 'Project Number', type: 'text' },
    { name: 'type_of_turbine', label: 'Type of Turbine', type: 'text' },
    { name: 'number_of_turbines', label: 'Number of Turbines', type: 'text' },
    { name: 'customer', label: 'Customer', type: 'text' },
    { name: 'max_days_per_employers', label: 'Max Days Per Employers', type: 'text' },
    { name: 'color', label: 'Project Color', type: 'color' },
    { name: 'comment', label: 'Comment', type: 'textarea' },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="w-full px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden mb-8"
        >
          <div className="px-6 py-4">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
            {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
            {success && <p className="text-green-500 mb-4 text-center">Project {editingProject ? 'updated' : 'added'} successfully!</p>}

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {inputFields.map((field, index) => (
                <motion.div
                  key={field.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={`flex flex-col ${field.name === 'comment' ? 'md:col-span-2' : ''}`}
                >
                  <label htmlFor={field.name} className="text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      id={field.name}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      className="px-3 py-2 placeholder-gray-400 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300 text-sm"
                      rows="3"
                    />
                  ) : (
                    <input
                      type={field.type}
                      id={field.name}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.type !== 'color' ? `Enter ${field.label.toLowerCase()}` : undefined}
                      required={field.name !== 'comment'}
                      className={`px-3 py-2 placeholder-gray-400 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300 text-sm ${field.type === 'color' ? 'h-10' : ''}`}
                    />
                  )}
                </motion.div>
              ))}

              {/* Turbine Coordinates Section */}
              {turbineCoordinates.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="md:col-span-2 mt-6"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    🌪️ Turbine Coordinates ({turbineCoordinates.length})
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {turbineCoordinates.map((turbine, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg shadow-sm border">
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Turbine Number
                              </label>
                              <input
                                type="text"
                                placeholder="e.g. Project-T1"
                                value={turbine.turbine_number}
                                onChange={(e) => handleTurbineCoordinateChange(index, 'turbine_number', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-300 font-medium"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Latitude
                              </label>
                              <input
                                type="number"
                                step="0.000001"
                                placeholder="e.g. 52.123456"
                                value={turbine.latitude}
                                onChange={(e) => handleTurbineCoordinateChange(index, 'latitude', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-300"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Longitude
                              </label>
                              <input
                                type="number"
                                step="0.000001"
                                placeholder="e.g. 16.789012"
                                value={turbine.longitude}
                                onChange={(e) => handleTurbineCoordinateChange(index, 'longitude', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-300"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 text-xs text-gray-500">
                      💡 Enter GPS coordinates for each turbine to display them on the LIVE map
                    </div>
                  </div>
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="md:col-span-2 mt-4"
              >
                <button
                  type="submit"
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition duration-300 text-sm font-medium"
                >
                  {editingProject ? 'Update Project' : 'Add Project'}
                </button>
              </motion.div>
            </form>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="w-full mx-auto bg-white shadow-lg rounded-lg overflow-hidden"
        >
          <div className="px-6 py-4">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Existing Projects</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {inputFields.map((field) => (
                      <th
                        key={field.name}
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {field.label}
                      </th>
                    ))}
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {projects.map((project) => (
                    <tr key={project.id}>
                      {inputFields.map((field) => (
                        <td
                          key={field.name}
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                        >
                          {field.name === 'color' ? (
                            <div className="flex items-center">
                              <div
                                className="w-6 h-6 mr-2 rounded-full"
                                style={{ backgroundColor: project[field.name] }}
                              ></div>
                              
                            </div>
                          ) : field.name === 'comment' ? (
                            <div className="max-w-xs overflow-hidden text-ellipsis">
                              {project[field.name]}
                            </div>
                          ) : (
                            project[field.name]
                          )}
                        </td>
                      ))}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(project)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(project.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default AddProject;
